import { useEffect, useState } from "react";
import { Row, Col, Input, InputGroup, InputGroupText, Button, Card } from "reactstrap";
import { Table, Column, AutoSizer, SortDirection } from "react-virtualized";
import { isRoleExternalAdmin, Theme } from "../../config/constants";
import PageHeader from "../../sharedComponents/Header/PageHeader";
import { BsFillPeopleFill, BsSearch } from "react-icons/bs";
import { sortableHeaderRenderer } from "../../sharedComponents/Tables/SortableHeaderRenderer";
import LocalStorage from "../../sharedComponents/LocalStorage/LocalStorage";
import { toast } from "react-toastify";
import userApi from "../../api/userApi";
import useUsersRoles from "./UsersRoles/useUsersRoles";
import moment from "moment";
import { BiSolidEdit } from 'react-icons/bi';
import EditUserSlideDrawer from "../../sharedComponents/SlideDrawer/EditUserSlideDrawer";
import { useDispatch, useSelector } from "react-redux";
import { slideDrawer } from "../../reducers/UIReducer/UIActions";
import useAdminLogin from "../../views/AdminLogin/useAdminLogin";
import MobileMenuSlideDrawer from "../../sharedComponents/SlideDrawer/MobileMenuSlideDrawer";

const UserManagement = () => {
    const dispatch = useDispatch();
    const isUserManager = isRoleExternalAdmin[LocalStorage.userData.get().user.title] || LocalStorage.userData.get().user.internalAdmin;
    const editSlideDrawerOpen = useSelector(({ui}) => ui.editUserSlideDrawer);
    const menuOpen = useSelector(({ ui }) => ui.mobileMenuSlideDrawer);
    const [userData, setUserData] = useState([]);
    const [inputText, setInputText] = useState('');
    const [selectedUser, setSelectedUser] = useState([]);
    const [sortBy, setSortBy] = useState('');
    const [sortDirection, setSortDirection] = useState(null);
    const adminLoginData = useAdminLogin();
    const manufacturer = LocalStorage.manufacturerData.get();
    const userRolesProps = useUsersRoles.useApi();
    const mfrId = LocalStorage.manufacturerData.get().platform_id;
    const isAdmin = LocalStorage.userData.get().user.internalAdmin === true;

    const getUsers = () => {
        const params = {
            manufacturer : manufacturer
        };
        userApi.post(params).then((response) => {
            setUserData(response.data)
        }).catch(err => {
            toast.error('Something went wrong fetching users');
            console.error(err);
        });
    }

    const onClickEditUser = (user) => {
        setSelectedUser(user);
        dispatch(slideDrawer.addEditUser());
    }

    const sortedUserTableData = (() => {
        if (!userData) return [];
        const sorted = [...userData];
        switch (sortBy) {
            case 'first_name' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.first_name.localeCompare(b.first_name));
                    } else {
                        return (b.first_name.localeCompare(a.first_name));
                    }
                });
                break;
            }
            case 'email' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.email.localeCompare(b.email));
                    } else {
                        return (b.email.localeCompare(a.email));
                    }
                });
                break;
            }
            case 'createdAt' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return moment(a.createdAt).diff(moment(b.createdAt));
                    } else {
                        return moment(b.createdAt).diff(moment(a.createdAt));
                    }
                });
                break;
            }
            case 'lastLoggedIn' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return moment(a.lastLoggedIn).diff(moment(b.lastLoggedIn));
                    } else {
                        return moment(b.lastLoggedIn).diff(moment(a.lastLoggedIn));
                    }
                });
                break;
            }
            case 'title' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.title.localeCompare(b.title));
                    } else {
                        return (b.title.localeCompare(a.title));
                    }
                });
                break;
            }
            default: {
                // Do nothing
            }
        }
        return sorted;
    })();

    const searchedUserTableData = (() => {
        if (userData?.length <= 0 || inputText === null || inputText === '') return [];
        const initialData = [...userData];
        const inputTextLowercase = inputText.toLowerCase();
        const searched = initialData.filter((rowData) => {
            return rowData?.first_name?.toLowerCase().includes(inputTextLowercase)
            || rowData?.last_name?.toLowerCase().includes(inputTextLowercase)
            || rowData?.email?.toLowerCase().includes(inputTextLowercase)
            || rowData?.title?.toLowerCase().includes(inputTextLowercase);
        });
        return searched;
    })();

    useEffect(() => {
        getUsers();
        if (menuOpen) {
            dispatch(slideDrawer.subtractMobileMenu());
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if(!editSlideDrawerOpen) {
            getUsers();
        }
        // eslint-disable-next-line
    }, [editSlideDrawerOpen])

    return (
        <>
            <Row>
                <Col>
                    <PageHeader title='User Management' subtitle='User List' icon={<BsFillPeopleFill fill='url(#icon-gradient)'/>} />
                </Col>
            </Row>
            <Card style={{height: 'calc(100vh - 124px)'}}>
                <Row className='mt-3 px-3'>
                    <Col className="w-fc">
                        <InputGroup size='sm'>
                            <InputGroupText>
                                <BsSearch/>
                            </InputGroupText>
                            <Input className='text-input' id="searchInput" placeholder='Search...' value={inputText} onChange={(e) => setInputText(e.target.value)}/>
                        </InputGroup>
                    </Col>
                    <Col></Col>
                    {isUserManager &&
                    <Col className="w-fc">
                        <Button
                            block
                            className='py-1 button-label'
                            style={{width: '80px'}}
                            size='sm' 
                            onClick={() => userRolesProps.userInvite(false, mfrId, getUsers)}
                            color={Theme().colors.accentName}>Invite User
                        </Button>
                    </Col>
                    }
                </Row>
                <Row className="px-3 mt-2 h-100">
                    <Col className="h-100">
                        <AutoSizer>
                            {({ width, height }) => (
                                <Table className="table usersTable border-grayLight" id="usersTable"
                                    width={width}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={(searchedUserTableData?.length > 0 || inputText !== '') ? searchedUserTableData.length : sortedUserTableData.length}
                                    rowGetter={({ index }) => (searchedUserTableData?.length > 0 || inputText !== '') ? searchedUserTableData[index] : sortedUserTableData[index]}
                                    sort={({ sortBy: sb, sortDirection: sd }) => {
                                        setSortBy(sb);
                                        setSortDirection(sd);
                                    }}
                                    sortBy={sortBy}
                                    sortDirection={sortDirection}
                                >
                                    <Column
                                        label='Name'
                                        dataKey='first_name'
                                        width={width * 0.2}
                                        headerRenderer={sortableHeaderRenderer}
                                        cellRenderer={({rowData}) => <span>{rowData.first_name} {rowData.last_name}</span>}
                                    />
                                    <Column
                                        label='Email'
                                        dataKey='email'
                                        width={width * 0.3}
                                        headerRenderer={sortableHeaderRenderer}
                                    />
                                    <Column
                                        label='Created Date'
                                        dataKey='createdAt'
                                        width={width * 0.15}
                                        headerRenderer={sortableHeaderRenderer}
                                        cellRenderer={({cellData}) =>
                                        <span>{moment(cellData).format('MMMM Do YYYY, h:mma')}</span>
                                        }
                                    />
                                    <Column
                                        label='Last Login Date'
                                        dataKey='lastLoggedIn'
                                        width={width * 0.2}
                                        headerRenderer={sortableHeaderRenderer}
                                        cellRenderer={({cellData}) =>
                                        <span>{moment(cellData).format('MMMM Do YYYY, h:mma')}</span>
                                        }
                                    />
                                    <Column
                                        label='Role'
                                        dataKey='title'
                                        width={width * 0.1}
                                        headerRenderer={sortableHeaderRenderer}
                                    />
                                    {isUserManager &&
                                    <Column
                                        dataKey='name'
                                        width={width * 0.05}
                                        cellRenderer={({rowData}) =>
                                            <div style={{cursor: 'pointer'}} onClick={() => onClickEditUser(rowData)}><BiSolidEdit size={16} color="#00A0FF"/></div>
                                        }
                                    />
                                    }
                                </Table>
                            )}
                        </AutoSizer>
                    </Col>
                </Row>
            </Card>
            <EditUserSlideDrawer user={selectedUser} manufacturer={mfrId} adminLoginData={adminLoginData} allowDelete={isAdmin} refreshCb={getUsers}/>
            <MobileMenuSlideDrawer />
        </>
    )
};

export default UserManagement;
